import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { Download, UploadFile } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/apiErrorUtils';
import { utcToDisplayDateAndTime, displayDateTimeToUTC, getTimezoneLabel } from '../../utils/timezoneUtils';
import { normalizePartNumber } from '../../utils/partNumber';
import {
  fetchInventoryByStoreCode,
  inventoryAutofillFields,
  inventoryRowToPartOption
} from '../../utils/inventoryPartLookup';
import { inventoryOptionLabel } from '../../utils/partMasterLabel';
import { requiresCorrectionNoteOnEdit } from '../../utils/purchaseOrderPermissions';
import { downloadCsvFile } from '../../utils/csvHelpers';
import { unitMetricLabel, unitSelectOptions } from '../../constants/unitMetrics';
import {
  INVENTORY_CATEGORIES,
  INVENTORY_CATEGORY_LABELS,
  normalizeInventoryCategory
} from '../../constants/inventoryCategories';

const AGENCIES_TAB_INDEX = 5;
const PO_LINE_ITEM_CSV_HEADERS = ['PO Number', 'PO Status', 'Part Number', 'Description', 'Category', 'Qty', 'Unit', 'Remarks'];
const PO_FORM_LINE_ITEM_CSV_HEADERS = ['Part Number', 'Description', 'Category', 'Qty', 'Unit', 'Remarks'];

const PurchaseOrderForm = ({ poId, initialData, onSaved, onCancel, displayTimezone }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [billToAgencies, setBillToAgencies] = useState([]);
  const [shipToAgencies, setShipToAgencies] = useState([]);
  const [operators, setOperators] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [masterModels, setMasterModels] = useState([]);
  const [partOptions, setPartOptions] = useState([]);
  const [loadingParts, setLoadingParts] = useState(false);
  const [clonePoOptions, setClonePoOptions] = useState([]);
  const [selectedClonePoId, setSelectedClonePoId] = useState('');
  const [loadingClonePoOptions, setLoadingClonePoOptions] = useState(false);
  const [cloningFromPo, setCloningFromPo] = useState(false);
  const [mappingPartLineIndex, setMappingPartLineIndex] = useState(null);
  const [lineSearchText, setLineSearchText] = useState({});
  const [addInventoryDialog, setAddInventoryDialog] = useState({
    open: false,
    lineIndex: null,
    partNumber: '',
    description: '',
    category: 'PART',
    storePartCode: ''
  });
  const [addingInventory, setAddingInventory] = useState(false);
  const [draftPoId, setDraftPoId] = useState(poId || '');
  const [poStatus, setPoStatus] = useState(null);
  const [correctionRemarks, setCorrectionRemarks] = useState('');
  const [draftSaveState, setDraftSaveState] = useState({ lastSavedAt: null, error: '' });
  const requiresCorrectionNote = requiresCorrectionNoteOnEdit(poStatus);
  const csvUploadRef = useRef(null);
  const inventorySearchTimeoutRef = useRef({});
  const PRIORITY_OPTIONS = [
    { value: 'CRITICAL', label: 'AOG (Aircraft On Ground) – Critical' },
    { value: 'HIGH', label: 'Rush (Urgent) – High' },
    { value: 'MEDIUM', label: 'Normal (Regular/Routine) – Medium' }
  ];
  const [form, setForm] = useState({
    orderTo: '',
    billTo: '',
    shipTo: '',
    operatorId: '',
    aircraftId: '',
    aircraftRegistration: '',
    aircraftModelVariant: '',
    aircraftSerialNumber: '',
    expectedDeliveryDate: '',
    priority: 'MEDIUM',
    notes: '',
    lineItems: [{ lineNumber: 1, partNumber: '', description: '', quantity: 1, unit: 'EACH', category: 'PART', remarks: '' }]
  });

  const isEdit = Boolean(poId);
  const effectivePoId = poId || draftPoId;
  const isDraftPo = !poStatus || poStatus === 'DRAFT';
  const showSaveDraft = isDraftPo && !requiresCorrectionNote;

  const buildFormFromPoData = (data) => ({
    orderTo: data.orderTo?._id || data.orderTo || '',
    billTo: data.billTo?._id || data.billTo || '',
    shipTo: data.shipTo?._id || data.shipTo || '',
    operatorId: data.operatorId?._id || data.operatorId || '',
    aircraftId: (() => {
      const id = data.aircraftId?._id || data.aircraftId;
      if (id) return String(id);
      if (String(data.aircraftRegistration || '').toUpperCase() === 'GEN') return 'GEN';
      return '';
    })(),
    aircraftRegistration: data.aircraftRegistration || '',
    aircraftModelVariant: data.aircraftModelVariant || '',
    aircraftSerialNumber: data.aircraftSerialNumber || '',
    expectedDeliveryDate: (() => {
      const raw = data.expectedDeliveryDate;
      if (!raw) return '';
      if (displayTimezone) {
        const d = utcToDisplayDateAndTime(raw, displayTimezone).date;
        return d || new Date(raw).toISOString().slice(0, 10);
      }
      return new Date(raw).toISOString().slice(0, 10);
    })(),
    priority: ['CRITICAL', 'HIGH', 'MEDIUM'].includes(data.priority) ? data.priority : 'MEDIUM',
    notes: data.notes || '',
    lineItems: (data.lineItems || []).length
      ? data.lineItems.map((l, i) => ({
          lineNumber: i + 1,
          partNumber: normalizePartNumber(l.partNumber || ''),
          description: l.description || '',
          quantity: l.quantity || 1,
          unit: l.unit || 'EACH',
          category: normalizeInventoryCategory(l.category),
          receivedQuantity: l.receivedQuantity,
          remarks: l.remarks || ''
        }))
      : [{ lineNumber: 1, partNumber: '', description: '', quantity: 1, unit: 'EACH', category: 'PART', remarks: '' }]
  });

  useEffect(() => {
    if (poId && !initialData) {
      axios.get(`/api/store/purchase-orders/${poId}`)
        .then((res) => {
          setPoStatus(res.data?.status || null);
          setForm(buildFormFromPoData(res.data));
        })
        .catch((err) => showToast(getErrorMessage(err, 'Failed to load purchase order.'), 'error'));
    }
  }, [poId, initialData, showToast, displayTimezone]);

  useEffect(() => {
    if (initialData) {
      setPoStatus(initialData.status || null);
    }
  }, [initialData]);

  useEffect(() => {
    setDraftPoId(poId || '');
  }, [poId]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [sup, bill, ship, op, ac, modelsRes] = await Promise.all([
          axios.get('/api/master-data/agencies', { params: { isActive: 'true', type: 'STORE_SUPPLIER' } }),
          axios.get('/api/master-data/agencies', { params: { isActive: 'true', type: 'BILL_TO' } }),
          axios.get('/api/master-data/agencies', { params: { isActive: 'true', type: 'SHIP_TO' } }),
          axios.get('/api/master-data/operators', { params: { isActive: 'true' } }),
          axios.get('/api/aircraft'),
          axios.get('/api/master-data/models', { params: { isActive: 'true', includePrimaryModels: 'true' } })
            .catch(() => ({ data: [] }))
        ]);
        setSuppliers(sup.data || []);
        setBillToAgencies(bill.data || []);
        setShipToAgencies(ship.data || []);
        setOperators(op.data || []);
        setAircraft(Array.isArray(ac.data) ? ac.data : (ac.data?.aircraft || ac.data?.data || []));
        const modelsPayload = modelsRes?.data;
        setMasterModels(
          Array.isArray(modelsPayload)
            ? modelsPayload
            : Array.isArray(modelsPayload?.data)
              ? modelsPayload.data
              : []
        );
      } catch (e) {
        console.error('Failed to load options', e);
        showToast('Failed to load form options', 'error');
      }
    };
    loadOptions();
  }, [showToast]);

  useEffect(() => {
    if (initialData) {
      setForm(buildFormFromPoData(initialData));
    }
  }, [initialData, displayTimezone]);

  useEffect(() => {
    if (isEdit) return;
    const loadClonePoOptions = async () => {
      setLoadingClonePoOptions(true);
      try {
        const res = await axios.get('/api/store/purchase-orders', {
          params: { includeArchived: 'true' }
        });
        setClonePoOptions(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        showToast(getErrorMessage(error, 'Failed to load previous purchase orders for cloning.'), 'error');
      } finally {
        setLoadingClonePoOptions(false);
      }
    };
    loadClonePoOptions();
  }, [isEdit, showToast]);

  const handleClonePoChange = async (clonePoId) => {
    setSelectedClonePoId(clonePoId || '');
    if (!clonePoId) return;
    setCloningFromPo(true);
    try {
      const res = await axios.get(`/api/store/purchase-orders/${clonePoId}`);
      setForm(buildFormFromPoData(res.data));
      setLineSearchText({});
      showToast(`Cloned from PO ${res.data?.poNumber || ''}`.trim(), 'success');
    } catch (error) {
      showToast(getErrorMessage(error, 'Failed to clone selected purchase order.'), 'error');
    } finally {
      setCloningFromPo(false);
    }
  };

  const handleAircraftChange = (aircraftId) => {
    if (String(aircraftId || '').toUpperCase() === 'GEN') {
      setForm((prev) => ({
        ...prev,
        aircraftId: 'GEN',
        aircraftRegistration: 'GEN',
        aircraftModelVariant: 'GEN',
        aircraftSerialNumber: '—'
      }));
      return;
    }
    const ac = aircraft.find((a) => String(a._id) === String(aircraftId));
    setForm((prev) => ({
      ...prev,
      aircraftId: aircraftId || '',
      aircraftRegistration: ac ? ac.registration : '',
      aircraftModelVariant: ac ? (ac.model || ac.modelVariant || '') : '',
      aircraftSerialNumber: ac ? ac.serialNumber : ''
    }));
  };

  const getItemId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value._id || value.id || '';
  };

  const isValidMongoObjectId = (id) => /^[a-f\d]{24}$/i.test(String(id || ''));

  const toMongoObjectIdOnly = (value) => {
    const id = getItemId(value);
    return isValidMongoObjectId(id) ? id : '';
  };

  const normalizeModelName = (name) => String(name || '').trim().toLowerCase().replace(/[\s_-]/g, '');

  const findMasterModelByName = (name) => {
    const target = normalizeModelName(name);
    if (!target || masterModels.length === 0) return null;
    return masterModels.find((model) => {
      const modelName = normalizeModelName(model.name);
      return modelName === target || modelName.includes(target) || target.includes(modelName);
    }) || null;
  };

  const resolveAircraftMasterModelIds = (selectedAircraft) => {
    const candidateNames = [
      selectedAircraft?.model,
      selectedAircraft?.modelVariant,
      form.aircraftModelVariant
    ].map((value) => String(value || '').trim()).filter(Boolean);

    let primaryModelId = '';
    let variantId = '';

    for (const name of candidateNames) {
      const match = findMasterModelByName(name);
      if (!match?._id) continue;
      if (match.primaryModel) {
        variantId = String(match._id);
      } else {
        primaryModelId = String(match._id);
      }
    }

    return { primaryModelId, variantId };
  };

  const getItemName = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    return String(value.name || value.label || value.code || value.value || '').trim();
  };

  const isPartApplicableToAircraft = (part, selectedAircraft) => {
    if (!selectedAircraft) return false;

    const aircraftModelId = getItemId(selectedAircraft.model);
    const aircraftVariantId = getItemId(selectedAircraft.modelVariant);
    const partModelIds = [
      getItemId(part.model),
      ...((part.primaryModels || []).map((m) => getItemId(m))),
      ...((part.modelVariants || []).map((m) => getItemId(m)))
    ].filter(Boolean);

    if (aircraftModelId && partModelIds.includes(aircraftModelId)) return true;
    if (aircraftVariantId && partModelIds.includes(aircraftVariantId)) return true;

    const aircraftNames = [
      getItemName(selectedAircraft.model),
      getItemName(selectedAircraft.modelVariant),
      String(selectedAircraft.model || '').trim(),
      String(selectedAircraft.modelVariant || '').trim()
    ]
      .filter(Boolean)
      .map((v) => v.toLowerCase());

    if (aircraftNames.length === 0) {
      return true;
    }

    const partNames = [
      getItemName(part.model),
      ...((part.primaryModels || []).map((m) => getItemName(m))),
      ...((part.modelVariants || []).map((m) => getItemName(m)))
    ]
      .filter(Boolean)
      .map((v) => v.toLowerCase());

    if (partNames.length === 0) return false;
    return partNames.some((name) => aircraftNames.some((airName) => name.includes(airName) || airName.includes(name)));
  };

  const isInventoryStubPart = (part) => {
    if (!part) return false;
    if (part.isInventoryStub === true) return true;
    const id = String(part._id || '');
    return id.startsWith('inventory-') && !isValidMongoObjectId(id);
  };

  const isCatalogPartOption = (part) => Boolean(part?.partNumber) && isValidMongoObjectId(part._id);

  const getPartOptionsByPartNumber = (partNumber) => {
    const normalized = normalizePartNumber(partNumber).toLowerCase();
    if (!normalized) return [];
    return (partOptions || []).filter(
      (p) => normalizePartNumber(p.partNumber).toLowerCase() === normalized
    );
  };

  const resolveCatalogPartForNumber = (partNumber) => {
    const catalogMatches = getPartOptionsByPartNumber(partNumber).filter(isCatalogPartOption);
    return catalogMatches[0] || null;
  };

  const fetchCatalogPartByNumber = async (partNumber) => {
    const normalized = normalizePartNumber(partNumber);
    if (!normalized) return null;
    const res = await axios.get('/api/parts', {
      params: { isActive: 'true', search: normalized, page: 1, pageSize: 20 },
      skipGlobalErrorToast: true
    });
    const parts = Array.isArray(res.data?.parts) ? res.data.parts : [];
    return (
      parts.find(
        (p) => normalizePartNumber(p.partNumber).toLowerCase() === normalized.toLowerCase()
      ) || null
    );
  };

  const hasPartOption = (partNumber) => getPartOptionsByPartNumber(partNumber).length > 0;

  const addInventoryStubOption = (partNumber, description = '') => {
    const normalizedPartNumber = normalizePartNumber(partNumber);
    if (!normalizedPartNumber) return;
    setPartOptions((prev) => {
      const exists = prev.some(
        (p) => normalizePartNumber(p.partNumber).toLowerCase() === normalizedPartNumber.toLowerCase()
      );
      if (exists) return prev;
      return [
        ...prev,
        {
          _id: `inventory-stub:${normalizedPartNumber}`,
          isInventoryStub: true,
          partNumber: normalizedPartNumber,
          partName: '',
          description: String(description || '').trim()
        }
      ];
    });
  };

  /** Aircraft registration for the current PO context ('GEN' for common stock). Used to key inventory store codes. */
  const resolveLineAircraftRegistration = () => {
    const isGenStock = String(form.aircraftId || '').toUpperCase() === 'GEN';
    if (isGenStock) return 'GEN';
    const selectedAircraft = aircraft.find((a) => String(a._id) === String(form.aircraftId));
    const registration = String(selectedAircraft?.registration || form.aircraftRegistration || '').trim().toUpperCase();
    return registration || 'GEN';
  };

  const buildInventoryCreatePayload = (partNumber, description = '', category = 'PART') => {
    const normalizedPartNumber = normalizePartNumber(partNumber);
    const isGenStock = String(form.aircraftId || '').toUpperCase() === 'GEN';
    const selectedAircraft = !isGenStock
      ? aircraft.find((a) => String(a._id) === String(form.aircraftId))
      : null;
    const registration = isGenStock
      ? 'GEN'
      : String(selectedAircraft?.registration || form.aircraftRegistration || '').trim().toUpperCase();
    const aircraftScope = isGenStock || !registration || registration === 'GEN' ? 'GEN' : 'AIRCRAFT';

    return {
      partNumber: normalizedPartNumber,
      description: String(description || '').trim() || normalizedPartNumber,
      category: normalizeInventoryCategory(category),
      quantity: 0,
      aircraftScope,
      aircraftId: aircraftScope === 'AIRCRAFT' ? (selectedAircraft?._id || form.aircraftId) : undefined,
      aircraftRegistration: aircraftScope === 'AIRCRAFT' ? registration : 'GEN'
    };
  };

  const addPartOptionIfMissing = (partNumber, description = '') => {
    addInventoryStubOption(partNumber, description);
  };

  const isInventoryItemOption = (part) => Boolean(part?.isInventoryItem);

  const partOptionPriority = (part) => {
    if (isCatalogPartOption(part)) return 3;
    if (isInventoryItemOption(part)) return 2;
    if (isInventoryStubPart(part)) return 1;
    return 0;
  };

  const dedupePartsByPartNumber = (parts = []) => {
    const byKey = new Map();
    for (const part of parts || []) {
      const key = normalizePartNumber(part?.partNumber).toLowerCase();
      if (!key) continue;
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, part);
        continue;
      }
      if (partOptionPriority(part) > partOptionPriority(existing)) {
        byKey.set(key, part);
      }
    }
    return Array.from(byKey.values());
  };

  const mergeInventoryOptions = (rows = []) => {
    const options = rows.map(inventoryRowToPartOption).filter(Boolean);
    if (options.length === 0) return;
    setPartOptions((prev) => dedupePartsByPartNumber([...prev, ...options]));
  };

  const applyInventoryToLine = (idx, inventoryRow) => {
    const fields = inventoryAutofillFields(inventoryRow);
    updateLine(idx, 'partNumber', fields.partNumber);
    if (fields.description) {
      updateLine(idx, 'description', fields.description);
    }
    if (fields.unitMetric) {
      updateLine(idx, 'unit', fields.unitMetric);
    }
    if (fields.category) {
      updateLine(idx, 'category', normalizeInventoryCategory(fields.category));
    }
    mergeInventoryOptions([inventoryRow]);
    setLineSearchText((prev) => ({ ...prev, [idx]: fields.partNumber }));
  };

  const searchInventoryForAutocomplete = (idx, searchText) => {
    const trimmed = String(searchText || '').trim();
    if (inventorySearchTimeoutRef.current[idx]) {
      clearTimeout(inventorySearchTimeoutRef.current[idx]);
    }
    if (trimmed.length < 2) return;

    inventorySearchTimeoutRef.current[idx] = setTimeout(async () => {
      try {
        const params = {
          includeArchived: false,
          search: trimmed,
          page: 1,
          pageSize: 50
        };
        if (form.aircraftId && String(form.aircraftId).toUpperCase() !== 'GEN') {
          params.aircraftId = form.aircraftId;
        }
        const res = await axios.get('/api/store/inventory', {
          params,
          skipGlobalErrorToast: true
        });
        const items = res.data?.items || res.data || [];
        mergeInventoryOptions(items);
      } catch (error) {
        console.error('Failed to search inventory for PO line', error);
      }
    }, 400);
  };

  const ensureInventoryPartExists = async (partNumber, description = '', options = {}) => {
    const normalizedPartNumber = normalizePartNumber(partNumber);
    if (!normalizedPartNumber || hasPartOption(normalizedPartNumber)) return false;

    const payload = buildInventoryCreatePayload(partNumber, description, options.category);

    // Do not auto-create GEN (common-stock) inventory stubs from the PO form.
    // GEN inventory rows must be added deliberately via the inventory form.
    if (payload.aircraftScope === 'GEN') return false;

    try {
      const res = await axios.post('/api/store/inventory', payload);
      const created = res?.data || {};
      addPartOptionIfMissing(created.partNumber || payload.partNumber, created.description || payload.description);
      if (!options.silent) {
        showToast(`Created new inventory item for ${payload.partNumber}`, 'success');
      }
      return true;
    } catch (error) {
      const status = error?.response?.status;
      const message = String(error?.response?.data?.message || '').toLowerCase();
      // If another request/process created it first, continue without blocking PO flow.
      if (
        status === 409
        || status === 422
        || status === 400
        || message.includes('already exists')
        || message.includes('already')
        || message.includes('store part code')
      ) {
        addPartOptionIfMissing(payload.partNumber, payload.description);
        return false;
      }
      if (!options.silent) {
        showToast(getErrorMessage(error, `Failed to auto-create inventory item for ${payload.partNumber}.`), 'error');
      }
      throw error;
    }
  };

  const isLinePartMappedToAircraft = (partNumber) => {
    const normalizedPartNumber = normalizePartNumber(partNumber);
    if (!form.aircraftId || String(form.aircraftId).toUpperCase() === 'GEN' || !normalizedPartNumber) return true;
    const selectedAircraft = aircraft.find((a) => String(a._id) === String(form.aircraftId));
    if (!selectedAircraft) return true;
    const catalogMatches = getPartOptionsByPartNumber(normalizedPartNumber).filter(isCatalogPartOption);
    if (catalogMatches.length === 0) return true;
    return catalogMatches.some((part) => isPartApplicableToAircraft(part, selectedAircraft));
  };

  const mapPartNumberToSelectedAircraft = async (partNumber, options = {}) => {
    const { lineIndex = null, silent = false } = options;
    if (!form.aircraftId || String(form.aircraftId).toUpperCase() === 'GEN') {
      return true;
    }
    const selectedAircraft = aircraft.find((a) => String(a._id) === String(form.aircraftId));
    if (!selectedAircraft) {
      if (!silent) showToast('Selected aircraft not found', 'error');
      return false;
    }

    const normalizedPartNumber = normalizePartNumber(partNumber);
    if (!normalizedPartNumber) return false;

    let partToMap = resolveCatalogPartForNumber(normalizedPartNumber);
    if (!partToMap) {
      try {
        partToMap = await fetchCatalogPartByNumber(normalizedPartNumber);
        if (partToMap) {
          setPartOptions((prev) => dedupePartsByPartNumber([...prev, partToMap]));
        }
      } catch (error) {
        console.error('Failed to load catalog part for aircraft mapping', error);
      }
    }
    if (!partToMap?._id || !isCatalogPartOption(partToMap)) {
      if (!silent) {
        showToast(
          `Part ${normalizedPartNumber} is not in the parts catalog. Add it under Parts master data to map it to this aircraft.`,
          'warning'
        );
      }
      return true;
    }

    const aircraftPrimaryModelId = toMongoObjectIdOnly(getItemId(selectedAircraft.model));
    const aircraftVariantId = toMongoObjectIdOnly(getItemId(selectedAircraft.modelVariant));
    const resolvedModelIds = resolveAircraftMasterModelIds(selectedAircraft);
    const targetPrimaryModelId = resolvedModelIds.primaryModelId || aircraftPrimaryModelId;
    const targetVariantId = resolvedModelIds.variantId || aircraftVariantId;
    if (!targetPrimaryModelId && !targetVariantId) {
      if (!silent) showToast('Cannot map automatically because aircraft model/variant master mapping is missing', 'error');
      return false;
    }

    const currentPrimaryModels = Array.isArray(partToMap.primaryModels)
      ? partToMap.primaryModels.map((m) => toMongoObjectIdOnly(m)).filter(Boolean)
      : [];
    const currentModelVariants = Array.isArray(partToMap.modelVariants)
      ? partToMap.modelVariants.map((m) => toMongoObjectIdOnly(m)).filter(Boolean)
      : [];

    const nextPrimaryModels = targetPrimaryModelId && !currentPrimaryModels.includes(targetPrimaryModelId)
      ? [...currentPrimaryModels, targetPrimaryModelId]
      : currentPrimaryModels;
    const nextModelVariants = targetVariantId && !currentModelVariants.includes(targetVariantId)
      ? [...currentModelVariants, targetVariantId]
      : currentModelVariants;

    if (
      nextPrimaryModels.length === currentPrimaryModels.length
      && nextModelVariants.length === currentModelVariants.length
    ) {
      return true;
    }

    if (typeof lineIndex === 'number') setMappingPartLineIndex(lineIndex);
    try {
      await axios.put(`/api/parts/${partToMap._id}`, {
        primaryModels: nextPrimaryModels,
        modelVariants: nextModelVariants
      }, { skipGlobalErrorToast: true });
      setPartOptions((prev) => prev.map((part) => {
        if (String(part._id) !== String(partToMap._id)) return part;
        return {
          ...part,
          primaryModels: nextPrimaryModels,
          modelVariants: nextModelVariants
        };
      }));
      if (!silent) showToast('Part mapped to selected aircraft', 'success');
      return true;
    } catch (error) {
      if (!silent) showToast(getErrorMessage(error, 'Failed to map part to selected aircraft.'), 'error');
      return false;
    } finally {
      if (typeof lineIndex === 'number') setMappingPartLineIndex(null);
    }
  };

  const handleMapPartToAircraft = async (line, lineIndex) =>
    mapPartNumberToSelectedAircraft(line?.partNumber, { lineIndex });

  const updateLine = (index, field, value) => {
    setForm((prev) => {
      const next = [...(prev.lineItems || [])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, lineItems: next };
    });
  };

  const escapeCsv = (value) => {
    const stringValue = String(value ?? '');
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const parseCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleDownloadLineItemsCsv = () => {
    const rows = (form.lineItems || [])
      .map((line) => ({ ...line, partNumber: normalizePartNumber(line.partNumber) }))
      .filter((line) => line.partNumber)
      .map((line) => ({
        'Part Number': line.partNumber,
        Description: line.description || '',
        Category: normalizeInventoryCategory(line.category),
        Qty: line.quantity ?? 1,
        Unit: line.unit || 'EACH',
        Remarks: line.remarks || ''
      }));
    downloadCsvFile(
      `purchase-order-line-items-${isEdit ? poId : 'draft'}.csv`,
      PO_FORM_LINE_ITEM_CSV_HEADERS,
      rows
    );
  };

  const handleDownloadAllPoLineItemsCsv = async () => {
    try {
      const res = await axios.get('/api/store/purchase-orders', {
        params: { includeArchived: 'true', lineItemExport: 'true' }
      });
      const pos = Array.isArray(res.data) ? res.data : [];
      const rows = [];
      for (const po of pos) {
        for (const line of po.lineItems || []) {
          const partNumber = normalizePartNumber(line.partNumber);
          if (!partNumber) continue;
          rows.push({
            'PO Number': po.poNumber || '',
            'PO Status': po.status || '',
            'Part Number': partNumber,
            Description: String(line.description || '').trim(),
            Category: normalizeInventoryCategory(line.category),
            Qty: line.quantity ?? 1,
            Unit: line.unit || 'EACH',
            Remarks: String(line.remarks || '').trim()
          });
        }
      }
      if (!rows.length) {
        showToast('No line items found across purchase orders', 'warning');
        return;
      }
      downloadCsvFile('all-purchase-order-line-items.csv', PO_LINE_ITEM_CSV_HEADERS, rows);
      showToast(`Downloaded ${rows.length} line item(s) from ${pos.length} purchase order(s)`, 'success');
    } catch (error) {
      showToast(getErrorMessage(error, 'Failed to download all purchase order line items.'), 'error');
    }
  };

  const handleUploadCsvClick = () => {
    csvUploadRef.current?.click();
  };

  const handleUploadLineItemsCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) {
        showToast('CSV has no line-item rows to import', 'error');
        return;
      }
      const header = parseCsvLine(lines[0]).map((h) => h.toLowerCase().trim());
      const pnIdx = header.findIndex((h) => h === 'part number' || h === 'partnumber' || h === 'pn');
      const descIdx = header.findIndex((h) => h === 'description' || h === 'desc');
      const qtyIdx = header.findIndex((h) => h === 'quantity' || h === 'qty');
      const unitIdx = header.findIndex((h) => h === 'unit');
      const categoryIdx = header.findIndex((h) => h === 'category');
      const remarksIdx = header.findIndex((h) => h === 'remarks' || h === 'remark');
      if (pnIdx < 0 || qtyIdx < 0) {
        showToast('CSV must include "Part Number" and "Qty" (or "Quantity") columns', 'error');
        return;
      }
      const imported = lines.slice(1).map((line) => parseCsvLine(line)).map((cols) => {
        const partNumber = normalizePartNumber(cols[pnIdx]);
        const quantity = Number(cols[qtyIdx]);
        const knownPart = partOptions.find(
          (p) => normalizePartNumber(p.partNumber).toLowerCase() === partNumber.toLowerCase()
        );
        const typedDescription = descIdx >= 0 ? String(cols[descIdx] || '').trim() : '';
        return {
          partNumber,
          description: String(knownPart?.description || '').trim() || typedDescription,
          quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
          unit: unitIdx >= 0 ? String(cols[unitIdx] || '').trim() || 'EACH' : 'EACH',
          category: normalizeInventoryCategory(
            categoryIdx >= 0 ? cols[categoryIdx] : (knownPart?.category || 'PART')
          ),
          remarks: remarksIdx >= 0 ? String(cols[remarksIdx] || '').trim() : ''
        };
      }).filter((line) => line.partNumber);

      if (!imported.length) {
        showToast('No valid line items found in CSV', 'error');
        return;
      }

      setForm((prev) => ({
        ...prev,
        lineItems: imported.map((line, idx) => ({
          lineNumber: idx + 1,
          partNumber: line.partNumber,
          description: line.description,
          quantity: line.quantity,
          unit: line.unit,
          category: line.category,
          remarks: line.remarks
        }))
      }));
      setLineSearchText({});
      showToast(`Imported ${imported.length} line item(s)`, 'success');
    } catch (err) {
      showToast('Failed to import CSV line items', 'error');
    } finally {
      event.target.value = '';
    }
  };

  const addLine = () => {
    setForm((prev) => ({
      ...prev,
      lineItems: [
        ...(prev.lineItems || []),
        {
          lineNumber: (prev.lineItems?.length || 0) + 1,
          partNumber: '',
          description: '',
          quantity: 1,
          unit: 'EACH',
          category: 'PART',
          remarks: ''
        }
      ]
    }));
  };

  const removeLine = (index) => {
    setForm((prev) => {
      const next = (prev.lineItems || []).filter((_, i) => i !== index);
      return { ...prev, lineItems: next.map((l, i) => ({ ...l, lineNumber: i + 1 })) };
    });
    setLineSearchText((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const key = parseInt(k, 10);
        if (Number.isNaN(key) || key === index) return;
        next[key > index ? key - 1 : key] = prev[k];
      });
      return next;
    });
  };

  useEffect(() => {
    const loadParts = async () => {
      setLoadingParts(true);
      try {
        const res = await axios.get('/api/parts', {
          params: { isActive: 'true', page: 1, pageSize: 2000 },
          skipGlobalErrorToast: true
        });
        const parts = Array.isArray(res.data?.parts) ? res.data.parts : Array.isArray(res.data) ? res.data : [];
        setPartOptions(dedupePartsByPartNumber(parts));
      } catch (e) {
        console.error('Failed to load parts for selected aircraft', e);
        showToast('Failed to load parts', 'error');
        setPartOptions([]);
      } finally {
        setLoadingParts(false);
      }
    };
    loadParts();
  }, [showToast]);

  useEffect(() => {
    const linePartNumbers = (form.lineItems || [])
      .map((line) => normalizePartNumber(line.partNumber))
      .filter(Boolean);
    const missing = linePartNumbers.filter((pn) => !hasPartOption(pn));
    if (missing.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const aircraftRegistration = resolveLineAircraftRegistration();
        const fetched = await Promise.all(
          missing.map(async (partNumber) => {
            const [catalogRes, inventoryRow] = await Promise.all([
              axios.get('/api/parts', {
                params: { isActive: 'true', search: partNumber, page: 1, pageSize: 20 },
                skipGlobalErrorToast: true
              }),
              fetchInventoryByStoreCode(partNumber, { aircraftRegistration }).catch(() => null)
            ]);
            const parts = Array.isArray(catalogRes.data?.parts) ? catalogRes.data.parts : [];
            const catalogMatches = parts.filter(
              (p) => normalizePartNumber(p.partNumber).toLowerCase() === partNumber.toLowerCase()
            );
            const inventoryOption = inventoryRow ? inventoryRowToPartOption(inventoryRow) : null;
            return [...catalogMatches, ...(inventoryOption ? [inventoryOption] : [])];
          })
        );
        if (cancelled) return;
        const additions = fetched.flat();
        if (additions.length > 0) {
          setPartOptions((prev) => dedupePartsByPartNumber([...prev, ...additions]));
        }
      } catch (e) {
        console.error('Failed to resolve PO line part catalog entries', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form.lineItems, form.aircraftId, partOptions]);

  const openAddInventoryDialog = (lineIndex) => {
    const typedValue = normalizePartNumber(
      lineSearchText[lineIndex] || form.lineItems?.[lineIndex]?.partNumber
    );
    setAddInventoryDialog({
      open: true,
      lineIndex,
      partNumber: typedValue,
      description: String(form.lineItems?.[lineIndex]?.description || '').trim(),
      category: normalizeInventoryCategory(form.lineItems?.[lineIndex]?.category),
      storePartCode: ''
    });
  };

  const handleAddToInventory = async () => {
    const normalizedDialogPartNumber = normalizePartNumber(addInventoryDialog.partNumber);
    const normalizedStoreCode = String(addInventoryDialog.storePartCode || '').trim().toUpperCase().replace(/\s+/g, '');
    if (!normalizedDialogPartNumber || !addInventoryDialog.description) {
      showToast('Part number and description are required', 'error');
      return;
    }
    if (addInventoryDialog.category === 'TOOL' && !normalizedStoreCode) {
      showToast('Store code is required for tools', 'error');
      return;
    }
    setAddingInventory(true);
    try {
      const payload = {
        ...buildInventoryCreatePayload(
          normalizedDialogPartNumber,
          addInventoryDialog.description.trim(),
          addInventoryDialog.category
        ),
        storePartCode: addInventoryDialog.category === 'TOOL' ? normalizedStoreCode : undefined
      };
      await axios.post('/api/store/inventory', payload);
      addInventoryStubOption(payload.partNumber, payload.description);
      if (typeof addInventoryDialog.lineIndex === 'number') {
        updateLine(addInventoryDialog.lineIndex, 'partNumber', payload.partNumber);
        updateLine(addInventoryDialog.lineIndex, 'description', payload.description);
        updateLine(addInventoryDialog.lineIndex, 'category', normalizeInventoryCategory(payload.category));
      }
      showToast('Part added to inventory', 'success');
      setAddInventoryDialog({ open: false, lineIndex: null, partNumber: '', description: '', category: 'PART', storePartCode: '' });
    } catch (error) {
      showToast(getErrorMessage(error, 'Failed to add part to inventory.'), 'error');
    } finally {
      setAddingInventory(false);
    }
  };

  const getSubmissionPayload = () => {
    const lines = (form.lineItems || [])
      .map((line) => ({ ...line, partNumber: normalizePartNumber(line.partNumber) }))
      .filter((line) => line.partNumber && line.quantity > 0);
    const payload = {
      orderTo: form.orderTo,
      billTo: form.billTo || undefined,
      shipTo: form.shipTo || undefined,
      operatorId: form.operatorId,
      aircraftId: form.aircraftId,
      expectedDeliveryDate: form.expectedDeliveryDate
        ? (displayTimezone ? displayDateTimeToUTC(form.expectedDeliveryDate, '00:00', displayTimezone) ?? form.expectedDeliveryDate : form.expectedDeliveryDate)
        : undefined,
      priority: form.priority || 'MEDIUM',
      notes: form.notes || '',
      lineItems: lines.map((l) => ({
        partNumber: normalizePartNumber(l.partNumber),
        description: l.description || '',
        quantity: l.quantity,
        unit: l.unit || 'EACH',
        category: normalizeInventoryCategory(l.category),
        remarks: l.remarks || '',
        reorderRequestId: l.reorderRequestId,
        receivedQuantity: l.receivedQuantity
      }))
    };
    return { lines, payload };
  };

  const validateSubmission = () => {
    if (!form.orderTo || !form.aircraftId) {
      showToast('Order To and Aircraft (or GEN) are required', 'error');
      return null;
    }
    if (!form.operatorId) {
      showToast('Operator is required', 'error');
      return null;
    }
    const { lines, payload } = getSubmissionPayload();
    if (lines.length === 0) {
      showToast('Add at least one line item with part number and quantity', 'error');
      return null;
    }
    if (requiresCorrectionNote && !String(correctionRemarks || '').trim()) {
      showToast('Please enter a reason for correction before saving changes during approval.', 'error');
      return null;
    }
    return { lines, payload };
  };

  const persistPurchaseOrder = async ({ stayOnPage = false } = {}) => {
    const validated = validateSubmission();
    if (!validated) return false;

    const { lines, payload } = validated;
    const setBusy = stayOnPage ? setSavingDraft : setLoading;
    setBusy(true);
    setDraftSaveState((prev) => ({ ...prev, error: '' }));

    try {
      for (const line of lines) {
        await ensureInventoryPartExists(line.partNumber, line.description, { silent: true });
        if (!isLinePartMappedToAircraft(line.partNumber)) {
          await mapPartNumberToSelectedAircraft(line.partNumber, { silent: true });
        }
      }

      let response;
      if (effectivePoId) {
        const updatePayload = requiresCorrectionNote
          ? { ...payload, correctionRemarks: String(correctionRemarks || '').trim() }
          : payload;
        response = await axios.put(`/api/store/purchase-orders/${effectivePoId}`, updatePayload);
      } else {
        response = await axios.post('/api/store/purchase-orders', payload);
      }

      if (response?.data?._id && !effectivePoId) {
        setDraftPoId(response.data._id);
      }
      if (response?.data?.status) {
        setPoStatus(response.data.status);
      }

      const savedAt = new Date();
      setDraftSaveState({ lastSavedAt: savedAt, error: '' });

      if (stayOnPage) {
        showToast(
          effectivePoId ? 'Draft saved' : `Draft saved${response?.data?.poNumber ? ` (${response.data.poNumber})` : ''}`,
          'success'
        );
      } else if (effectivePoId) {
        showToast(
          requiresCorrectionNote ? 'Purchase order corrected and saved' : 'Purchase order updated',
          'success'
        );
        onSaved && onSaved();
      } else {
        showToast('Purchase order created', 'success');
        onSaved && onSaved();
      }
      return true;
    } catch (error) {
      const message = getErrorMessage(error, stayOnPage ? 'Failed to save draft' : 'Save failed. Please try again.');
      if (stayOnPage) {
        setDraftSaveState((prev) => ({ ...prev, error: message }));
      }
      showToast(message, 'error');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSaveDraft = () => persistPurchaseOrder({ stayOnPage: true });

  const handleSubmit = () => persistPurchaseOrder({ stayOnPage: false });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {isEdit
          ? (requiresCorrectionNote ? 'Correct Purchase Order (approval)' : 'Edit Purchase Order')
          : 'New Purchase Order'}
      </Typography>
      {requiresCorrectionNote && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Approval correction</AlertTitle>
          This purchase order is awaiting approval. Describe why you are changing it before you save.
        </Alert>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {!isEdit && (
            <Grid size={12}>
              <FormControl fullWidth disabled={loadingClonePoOptions || cloningFromPo}>
                <InputLabel>Clone From PO</InputLabel>
                <Select
                  value={selectedClonePoId}
                  label="Clone From PO"
                  onChange={(e) => handleClonePoChange(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {clonePoOptions.map((po) => (
                    <MenuItem key={po._id} value={po._id}>
                      {po.poNumber} - {po.status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid size={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <FormControl fullWidth required>
                <InputLabel>Order To (Supplier)</InputLabel>
                <Select
                  value={form.orderTo}
                  label="Order To (Supplier)"
                  onChange={(e) => setForm({ ...form, orderTo: e.target.value })}
                >
                  <MenuItem value="">Select</MenuItem>
                  {suppliers.map((s) => (
                    <MenuItem key={s._id} value={s._id}>{s.name} ({s.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => navigate('/master-data', { state: { openTab: AGENCIES_TAB_INDEX } })}
                sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
              >
                Add Supplier
              </Button>
            </Box>
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>Bill To</InputLabel>
              <Select
                value={form.billTo}
                label="Bill To"
                onChange={(e) => setForm({ ...form, billTo: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {billToAgencies.map((a) => (
                  <MenuItem key={a._id} value={a._id}>{a.name} ({a.code})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>Ship To</InputLabel>
              <Select
                value={form.shipTo}
                label="Ship To"
                onChange={(e) => setForm({ ...form, shipTo: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {shipToAgencies.map((a) => (
                  <MenuItem key={a._id} value={a._id}>{a.name} ({a.code})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>Operator</InputLabel>
              <Select
                value={form.operatorId}
                label="Operator"
                onChange={(e) => setForm({ ...form, operatorId: e.target.value })}
              >
                <MenuItem value="">Select</MenuItem>
                {operators.map((o) => (
                  <MenuItem key={o._id} value={o._id}>{o.name} ({o.code})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>Aircraft</InputLabel>
              <Select
                value={form.aircraftId}
                label="Aircraft"
                onChange={(e) => handleAircraftChange(e.target.value)}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="GEN">GEN (general stock)</MenuItem>
                {aircraft.map((a) => (
                  <MenuItem key={a._id} value={a._id}>
                    {a.registration} {a.model ? `– ${a.model}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Registration"
              value={form.aircraftRegistration}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Model Variant"
              value={form.aircraftModelVariant}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Serial Number"
              value={form.aircraftSerialNumber}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="date"
              label={displayTimezone ? `Expected Delivery (${getTimezoneLabel(displayTimezone)})` : 'Expected Delivery'}
              value={form.expectedDeliveryDate}
              onChange={(e) => setForm({ ...form, expectedDeliveryDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={form.priority || 'MEDIUM'}
                label="Priority"
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Line Items</Typography>
      <TableContainer component={Paper} sx={{ mb: 2, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 1400 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }}>#</TableCell>
              <TableCell sx={{ minWidth: 260 }}>Part Number</TableCell>
              <TableCell sx={{ minWidth: 360 }}>Description</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Category</TableCell>
              <TableCell sx={{ minWidth: 110 }}>Qty</TableCell>
              <TableCell sx={{ minWidth: 130 }}>Unit</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Remarks</TableCell>
              <TableCell width={50}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(form.lineItems || []).map((line, idx) => (
              <TableRow key={idx} sx={{ verticalAlign: 'top' }}>
                <TableCell sx={{ pt: 2 }}>{line.lineNumber}</TableCell>
                <TableCell>
                  <Autocomplete
                    fullWidth
                    options={partOptions}
                    loading={loadingParts}
                    getOptionLabel={(option) => normalizePartNumber(option?.partNumber)}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      const label = option?.isInventoryItem
                        ? inventoryOptionLabel(option)
                        : (() => {
                            const partNumber = normalizePartNumber(option?.partNumber);
                            const partName = String(option?.partName || '').trim();
                            return partName ? `${partNumber} - ${partName}` : partNumber;
                          })();
                      return (
                        <li key={key} {...optionProps}>
                          {label}
                        </li>
                      );
                    }}
                    isOptionEqualToValue={(option, value) =>
                      normalizePartNumber(option?.partNumber).toLowerCase() === normalizePartNumber(value?.partNumber).toLowerCase()
                    }
                    value={
                      partOptions.find(
                        (p) => normalizePartNumber(p.partNumber).toLowerCase() === normalizePartNumber(line.partNumber).toLowerCase()
                      ) || null
                    }
                    inputValue={lineSearchText[idx] ?? normalizePartNumber(line.partNumber) ?? ''}
                    onInputChange={(event, value, reason) => {
                      if (reason === 'clear') {
                        setLineSearchText((prev) => ({ ...prev, [idx]: '' }));
                        return;
                      }
                      // Ignore Autocomplete "reset" (e.g. after clone/value resolve) so inventory
                      // option labels like "PN — DESC (Qty: n)" never overwrite the part number.
                      if (reason !== 'input') return;
                      setLineSearchText((prev) => ({ ...prev, [idx]: value || '' }));
                      searchInventoryForAutocomplete(idx, value);
                    }}
                    onChange={(event, selected) => {
                      const normalizedSelectedPartNumber = normalizePartNumber(selected?.partNumber);
                      updateLine(idx, 'partNumber', normalizedSelectedPartNumber);
                      updateLine(idx, 'description', selected?.description || line.description || '');
                      if (selected?.unitMetric) {
                        updateLine(idx, 'unit', selected.unitMetric);
                      }
                      if (selected?.category) {
                        updateLine(idx, 'category', normalizeInventoryCategory(selected.category));
                      }
                      setLineSearchText((prev) => ({ ...prev, [idx]: normalizedSelectedPartNumber }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search part number"
                        onBlur={async (event) => {
                          if (typeof params?.inputProps?.onBlur === 'function') {
                            params.inputProps.onBlur(event);
                          }
                          const linePartNumber = normalizePartNumber(line.partNumber);
                          if (linePartNumber && getPartOptionsByPartNumber(linePartNumber)[0]) {
                            setLineSearchText((prev) => ({ ...prev, [idx]: linePartNumber }));
                            return;
                          }

                          const typedValue = normalizePartNumber(lineSearchText[idx] ?? line.partNumber ?? '');
                          if (!typedValue) return;

                          const existingOption = getPartOptionsByPartNumber(typedValue)[0];
                          if (existingOption) {
                            if (!String(line.description || '').trim() && existingOption.description) {
                              updateLine(idx, 'description', existingOption.description);
                            }
                            if (existingOption.unitMetric) {
                              updateLine(idx, 'unit', existingOption.unitMetric);
                            }
                            if (existingOption.category) {
                              updateLine(idx, 'category', normalizeInventoryCategory(existingOption.category));
                            }
                            updateLine(idx, 'partNumber', typedValue);
                            setLineSearchText((prev) => ({ ...prev, [idx]: typedValue }));
                            return;
                          }

                          try {
                            const inventoryMatch = await fetchInventoryByStoreCode(typedValue, {
                              aircraftRegistration: resolveLineAircraftRegistration()
                            });
                            if (inventoryMatch) {
                              applyInventoryToLine(idx, inventoryMatch);
                              return;
                            }

                            await ensureInventoryPartExists(typedValue, line.description, {
                              silent: true,
                              category: line.category
                            });
                            updateLine(idx, 'partNumber', typedValue);
                            if (!String(line.description || '').trim()) {
                              updateLine(idx, 'description', typedValue);
                            }
                            setLineSearchText((prev) => ({ ...prev, [idx]: typedValue }));
                          } catch (error) {
                            // Keep form editable even if auto-create fails.
                          }
                        }}
                      />
                    )}
                  />
                  {(() => {
                    const linePartNumber = normalizePartNumber(line.partNumber);
                    if (
                      linePartNumber &&
                      partOptions.some(
                        (p) =>
                          normalizePartNumber(p.partNumber).toLowerCase() === linePartNumber.toLowerCase()
                      )
                    ) {
                      return null;
                    }

                    const typedRaw = lineSearchText[idx] ?? '';
                    if (!String(typedRaw).trim()) return null;

                    const typed = normalizePartNumber(typedRaw).toLowerCase();
                    const hasMatch = partOptions.some((p) => {
                      const partNo = normalizePartNumber(p.partNumber).toLowerCase();
                      const partName = String(p.partName || '').toLowerCase();
                      return partNo === typed || partNo.includes(typed) || partName.includes(typed);
                    });
                    if (hasMatch) return null;
                    return (
                      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Part not found in parts catalog or inventory.
                        </Typography>
                        <Button size="small" onClick={() => openAddInventoryDialog(idx)}>
                          Add to Inventory
                        </Button>
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    value={line.description}
                    onChange={(e) => updateLine(idx, 'description', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    fullWidth
                    value={normalizeInventoryCategory(line.category)}
                    onChange={(e) => updateLine(idx, 'category', e.target.value)}
                    sx={{ minWidth: 150 }}
                  >
                    {INVENTORY_CATEGORIES.map((category) => (
                      <MenuItem key={category} value={category}>
                        {INVENTORY_CATEGORY_LABELS[category]}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0.001, step: 'any' }}
                    value={line.quantity}
                    onChange={(e) => updateLine(idx, 'quantity', parseFloat(e.target.value) || 1)}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    fullWidth
                    value={line.unit || 'EACH'}
                    onChange={(e) => updateLine(idx, 'unit', e.target.value)}
                    sx={{ minWidth: 130 }}
                  >
                    {unitSelectOptions(line.unit).map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unitMetricLabel(unit)}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    value={line.remarks}
                    onChange={(e) => updateLine(idx, 'remarks', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => removeLine(idx)} disabled={(form.lineItems || []).length <= 1}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button startIcon={<Add />} onClick={addLine}>Add line</Button>
        <Button startIcon={<Download />} variant="outlined" onClick={handleDownloadLineItemsCsv}>
          Download CSV
        </Button>
        <Button startIcon={<Download />} variant="outlined" onClick={handleDownloadAllPoLineItemsCsv}>
          Download all PO items
        </Button>
        <Button startIcon={<UploadFile />} variant="outlined" onClick={handleUploadCsvClick}>
          Upload CSV
        </Button>
        <input
          type="file"
          ref={csvUploadRef}
          onChange={handleUploadLineItemsCsv}
          accept=".csv,text/csv"
          style={{ display: 'none' }}
        />
      </Box>

      {requiresCorrectionNote && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            fullWidth
            required
            label="Reason for correction"
            placeholder="Explain what was changed and why (required for approval edits)"
            value={correctionRemarks}
            onChange={(e) => setCorrectionRemarks(e.target.value)}
            multiline
            minRows={3}
          />
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {showSaveDraft && (
          <Button variant="outlined" onClick={handleSaveDraft} disabled={loading || savingDraft}>
            {savingDraft ? 'Saving draft…' : 'Save draft'}
          </Button>
        )}
        <Button variant="contained" onClick={handleSubmit} disabled={loading || savingDraft}>
          {isEdit ? (requiresCorrectionNote ? 'Save correction' : 'Update') : 'Create'}
        </Button>
        {onCancel && <Button onClick={onCancel} disabled={loading || savingDraft}>Cancel</Button>}
      </Box>
      {showSaveDraft && (
        <Typography variant="body2" color={draftSaveState.error ? 'error.main' : 'text.secondary'} sx={{ mt: 1 }}>
          {draftSaveState.error
            ? draftSaveState.error
            : draftSaveState.lastSavedAt
              ? `Draft saved at ${draftSaveState.lastSavedAt.toLocaleTimeString()}`
              : 'Use Save draft to keep your work without leaving this page.'}
        </Typography>
      )}
      {requiresCorrectionNote && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Enter a reason and click Save correction.
        </Typography>
      )}

      <Dialog
        open={addInventoryDialog.open}
        onClose={() => setAddInventoryDialog({ open: false, lineIndex: null, partNumber: '', description: '', category: 'PART', storePartCode: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Part to Inventory</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Part Number"
                value={addInventoryDialog.partNumber}
                onChange={(e) => setAddInventoryDialog((prev) => ({ ...prev, partNumber: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                value={addInventoryDialog.description}
                onChange={(e) => setAddInventoryDialog((prev) => ({ ...prev, description: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={addInventoryDialog.category}
                  label="Category"
                  onChange={(e) => setAddInventoryDialog((prev) => ({ ...prev, category: e.target.value }))}
                >
                  {INVENTORY_CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {INVENTORY_CATEGORY_LABELS[category]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {addInventoryDialog.category === 'TOOL' && (
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Store Code"
                  value={addInventoryDialog.storePartCode}
                  onChange={(e) => setAddInventoryDialog((prev) => ({ ...prev, storePartCode: e.target.value }))}
                  helperText="Required for tools"
                  required
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddInventoryDialog({ open: false, lineIndex: null, partNumber: '', description: '', category: 'PART', storePartCode: '' })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddToInventory} disabled={addingInventory}>
            {addingInventory ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrderForm;
